/**
 * Session recovery — persists the minimal user context in sessionStorage
 * so we can rehydrate it after an auto-reload triggered by the
 * GlobalErrorBoundary or by a deploy upgrade.
 */

const KEY = "ss2026_recovery_snapshot_v1";
const RECOVERY_FLAG = "ss2026_recovery_attempted_v1";

export interface RecoverySnapshot {
  path: string;
  matchId: string | null;
  language: string | null;
  nation: string | null;
  activeTab: string | null;
  at: number;
}

function readJSON<T>(key: string, storage: Storage): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function captureRecoverySnapshot(): void {
  if (typeof window === "undefined") return;
  try {
    const snap: RecoverySnapshot = {
      path: window.location.pathname + window.location.search + window.location.hash,
      matchId: localStorage.getItem("ss2026_active_match_id"),
      language: localStorage.getItem("ss2026_language"),
      nation: localStorage.getItem("ss2026_selected_nation"),
      activeTab: sessionStorage.getItem("ss2026_active_tab"),
      at: Date.now(),
    };
    sessionStorage.setItem(KEY, JSON.stringify(snap));
  } catch {
    /* storage full / private mode — skip */
  }
}

export function consumeRecoverySnapshot(): RecoverySnapshot | null {
  if (typeof window === "undefined") return null;
  const snap = readJSON<RecoverySnapshot>(KEY, sessionStorage);
  if (!snap) return null;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
  // Stale > 60s, ignore.
  if (Date.now() - snap.at > 60_000) return null;
  return snap;
}

export function hasAttemptedRecovery(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return sessionStorage.getItem(RECOVERY_FLAG) === "1";
  } catch {
    return true;
  }
}

export function markRecoveryAttempted(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(RECOVERY_FLAG, "1");
  } catch {
    /* noop */
  }
}

export function clearRecoveryAttempt(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(RECOVERY_FLAG);
  } catch {
    /* noop */
  }
}